/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Field, useFormikContext } from "formik";
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import __ from "web/Layout/Translations";

import ValidationErrorMessage from "web/Components/Common/ValidationErrorMessage";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import sendGtmEvent from "web/utils/system/GTM/sendGtmEvent";

import { gtmFiltersKeys, location } from "web/constants/filters";

import { Nullable } from "web/types/Utils";

import { useGoogleContext } from "web/context/google.context";

import ClearInputFieldWrapper from "../ClearInputFieldWrapper";
import classes from "./googleLocalization.scss";

interface IInitialValue {
  placeId: string;
}

interface IGoogleLocalizationProps {
  name: string;
  disabled?: boolean;
  placeholder?: string;
  initialValue?: number | string | IInitialValue;
  fromMobileFilters?: boolean;
}

const GoogleLocalization: FC<IGoogleLocalizationProps> = ({
  disabled = false,
  name,
  placeholder = "",
  initialValue = "",
  fromMobileFilters = "",
}) => {
  const { autocompleteService, geocoderService } = useGoogleContext();
  const [inputValue, setInputValue] = useState("");
  const [openStatus, setOpenStatus] = useState(false);
  const [predictionValues, setPredictionValues] = useState<
    Nullable<google.maps.places.AutocompletePrediction[]>
  >([]);
  const { setFieldValue, errors, touched } = useFormikContext();
  const timeoutRef = useRef<Nullable<NodeJS.Timeout>>(null);
  const isError =
    errors[name as keyof typeof errors] &&
    touched[name as keyof typeof touched];
  const onChangeHandler = (syntheticEvent: SyntheticEvent) => {
    syntheticEvent.preventDefault();
    const nextValue =
      syntheticEvent &&
      syntheticEvent.currentTarget &&
      (syntheticEvent.currentTarget as HTMLInputElement).value;
    clearTimeout(timeoutRef.current as NodeJS.Timeout);
    setInputValue(nextValue);
    timeoutRef.current = setTimeout(() => {
      if (nextValue) {
        autocompleteService?.getPlacePredictions(
          {
            input: nextValue,
            componentRestrictions: {
              country: "pl",
            },
            types: ["(regions)"],
          },
          (predictions, status) => {
            if (status === "OK") {
              setOpenStatus(true);
              setPredictionValues(predictions);
            } else {
              setOpenStatus(true);
              setPredictionValues([]);
            }
          }
        );
      } else {
        setFieldValue(name, null);
        setOpenStatus(false);
      }
    }, 500);
  };
  const onKeyPressHandler = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key === "Enter" &&
        openStatus &&
        isArrayHasItems(predictionValues) &&
        predictionValues[0] &&
        predictionValues[0].place_id
      ) {
        event.preventDefault();
        geocoderService?.geocode(
          {
            placeId: predictionValues[0].place_id,
          },
          (result, status) => {
            if (status === "OK" && isArrayHasItems(result) && result[0]) {
              setInputValue(result[0].formatted_address);
              setFieldValue(name, {
                lat: result[0].geometry.location.lat(),
                lng: result[0].geometry.location.lng(),
                placeId: predictionValues[0].place_id,
              });
              setOpenStatus(false);
            }
          }
        );
      }
    },
    [setInputValue, setFieldValue, setOpenStatus, openStatus, predictionValues]
  );
  const keypressEscapeHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenStatus(false);
      }
    },
    [setOpenStatus]
  );
  const clickOutsideHandler = useCallback(
    (event: globalThis.MouseEvent) => {
      if (
        event &&
        event.target &&
        typeof (event.target as HTMLElement).closest === "function" &&
        !(event.target as HTMLElement).closest(`.${classes.root}`)
      ) {
        setOpenStatus(false);
      }
    },
    [setOpenStatus]
  );

  useEffect(() => {
    if (openStatus) {
      document.addEventListener("keydown", keypressEscapeHandler);
      document.addEventListener("click", clickOutsideHandler);
    } else {
      document.removeEventListener("keydown", keypressEscapeHandler);
      document.removeEventListener("click", clickOutsideHandler);
    }

    return () => {
      document.removeEventListener("keydown", keypressEscapeHandler);
      document.removeEventListener("click", clickOutsideHandler);
    };
  }, [openStatus, clickOutsideHandler, keypressEscapeHandler]);

  useEffect(() => {
    let isMounted = true;

    if (
      initialValue &&
      (initialValue as IInitialValue).placeId &&
      geocoderService
    ) {
      geocoderService.geocode(
        {
          placeId: (initialValue as IInitialValue).placeId,
        },
        (result, status) => {
          if (
            status === "OK" &&
            isArrayHasItems(result) &&
            result[0] &&
            result[0].formatted_address &&
            isMounted
          ) {
            setInputValue(result[0].formatted_address);
          }
        }
      );
    }

    return () => {
      isMounted = false;
    };
  }, [geocoderService]);

  useEffect(() => {
    if (inputValue && !initialValue) {
      setInputValue("");
    }
  }, [initialValue]);

  const clearField = () => {
    setInputValue("");
    setFieldValue(name, null);
    setOpenStatus(false);
  };

  return (
    <div
      className={`${classes.root} ${
        fromMobileFilters ? classes.fromMobileFilters : ""
      }`}
    >
      <div className={classes.field}>
        <ClearInputFieldWrapper value={inputValue} clearField={clearField}>
          <input
            className={`${classes.input} ${isError ? classes.error : ""} ${
              fromMobileFilters ? classes.fromMobileFilters : ""
            }`}
            data-t1="geolocation_search_input"
            data-t2={name}
            type="text"
            placeholder={__(placeholder)}
            disabled={disabled}
            value={inputValue}
            onChange={onChangeHandler}
            // @ts-ignore
            onKeyPress={onKeyPressHandler}
            id={name}
          />
        </ClearInputFieldWrapper>
      </div>
      {openStatus ? (
        <div className={classes.autocomplete}>
          {isArrayHasItems(predictionValues) ? (
            <ul className={classes.list}>
              {predictionValues.map((predictionValue) => {
                return (
                  <li className={classes.item} key={predictionValue.place_id}>
                    <button
                      className={classes.button}
                      type="button"
                      onClick={() => {
                        geocoderService?.geocode(
                          {
                            placeId: predictionValue.place_id,
                          },
                          (result, status) => {
                            if (
                              status === "OK" &&
                              isArrayHasItems(result) &&
                              result[0]
                            ) {
                              setInputValue(result[0].formatted_address);
                              setFieldValue(name, {
                                lat: result[0].geometry.location.lat(),
                                lng: result[0].geometry.location.lng(),
                                placeId: predictionValue.place_id,
                              });
                              setOpenStatus(false);
                              const gtmOptions = {
                                eventCategory: location.attribute_label,
                                [gtmFiltersKeys.location_province]:
                                  result[0].formatted_address,
                              };
                              sendGtmEvent("filtrujProdukty", gtmOptions, true);
                            }
                          }
                        );
                      }}
                    >
                      {predictionValue.description}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={classes.empty}>
              {__("Brak wyników wyszukiwania")}
            </div>
          )}
        </div>
      ) : null}
      <Field value="" className="visually-hidden" type="hidden" name={name} />
      {isError && <ValidationErrorMessage name={name} />}
    </div>
  );
};

export default GoogleLocalization;
